// Vehicle photos. A booking stores which catalog model the vehicle is; the
// photo comes from the catalog, looked up here, never copied onto the group.

export const BODY_TYPES = [
  { value: "suv", label: "SUV" },
  { value: "hatchback", label: "Hatchback" },
  { value: "sedan", label: "Sedan" },
  { value: "mpv", label: "MPV" },
  { value: "other", label: "Other" },
];

export const bodyTypeLabel = (value) => BODY_TYPES.find((b) => b.value === value)?.label || "Other";

// Catalog rows by id, for O(1) lookups while rendering a list of groups.
export function indexCatalog(rows) {
  const map = new Map();
  for (const r of rows || []) map.set(Number(r.id), r);
  return map;
}

// What to draw for a group's vehicle. Falls back in three steps: the catalog
// photo; otherwise a generic picture for the body type (when the model is
// known but has no photo yet); otherwise nothing, and the caller shows the
// customer's initials as before.
export function vehicleVisual(group, catalogById) {
  const id = Number(group?.["Vehicle Catalog ID"]);
  const model = id && catalogById ? catalogById.get(id) : null;
  if (model?.thumb_url) {
    return { kind: "photo", thumb: model.thumb_url, hero: model.hero_url || model.thumb_url, bodyType: model.body_type };
  }
  if (model) return { kind: "placeholder", bodyType: model.body_type || "other" };
  return { kind: "none" };
}

// "Grand Vitara · Zeta AT · Petrol · 2023", skipping what isn't known.
export function vehicleSubtitle(group) {
  const parts = [
    group?.["Vehicle Model"],
    group?.["Vehicle Variant"],
    group?.["Vehicle Fuel"],
    group?.["Vehicle Year"],
  ]
    .map((v) => String(v ?? "").trim())
    .filter(Boolean);
  return parts.join(" · ");
}

// ---- Booking picker -------------------------------------------------------------

export const EMPTY_VEHICLE = { catalogId: null, typed: "", notListed: false, variant: "", fuel: "", year: "" };

// Type-to-search over the catalog: every word typed must appear in the name,
// so "grand z" still finds "Grand Vitara Zeta". An empty query shows everything.
export function filterVehicles(rows, query) {
  const words = String(query || "").toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return rows || [];
  return (rows || []).filter((r) => {
    const name = String(r.name || "").toLowerCase();
    return words.every((w) => name.includes(w));
  });
}

// The booking needs either a catalog pick or, under "Not listed", a typed name.
// Returns an error message, or "" when the choice is complete.
export function vehicleChoiceError(v) {
  if (v.notListed) return v.typed.trim() ? "" : "Type the vehicle model.";
  return v.catalogId ? "" : "Pick the vehicle model from the list.";
}

// The fields the create-group API takes, with blanks left out (the API treats a
// missing field as "not given", but rejects an empty string for a number).
export function vehiclePayload(v) {
  const out = {};
  if (v.notListed) {
    if (v.typed.trim()) out.vehicle_model_typed = v.typed.trim();
  } else if (v.catalogId) {
    out.vehicle_catalog_id = Number(v.catalogId);
  }
  if (v.variant.trim()) out.vehicle_variant = v.variant.trim();
  if (v.fuel.trim()) out.vehicle_fuel = v.fuel.trim();
  const year = parseInt(v.year, 10);
  if (Number.isFinite(year)) out.vehicle_year = year;
  return out;
}
